import { Tooltip, Typography } from '@material-ui/core';
import { Check, Eye, HelpCircle } from 'lucide-react';
import React from 'react';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useTheme } from '../../../../themes';
import { ActorFilterChipButton } from '../Actors';
import { ActorChip } from '../Builders/newux/kit';
import { SegmentDescriptor } from '../ExperienceTimeline/segmentFilter';

export { useTemplateSegments } from './useTemplateSegments';

/**
 * One pill of the "Filter by Segment" row. Reuses the actor filter's button
 * shell and hover-eye mechanic (same af-icon/af-eye CSS classes on
 * ActorFilterChipButton) for visual parity with the actor row, but always in
 * the tinted / dark-text treatment: audience colors are light tints on which
 * the classic white-text pill is unreadable.
 *
 * The label and the eye each get their OWN tooltip, as siblings — not one
 * wrapping the whole button with the eye's tooltip nested inside it (that's
 * what the actor row's tinted variant does, and it double-fires: once the
 * eye fades in on hover it sits inside the outer tooltip's hover region, so
 * both tooltips end up open at once). The classic (non-tinted) actor pill
 * avoids exactly this by tooltipping the label and the eye separately, which
 * is the structure this mirrors.
 *
 * The row is single-select, so clicking the pill body and clicking its eye
 * both end up "showing only" this segment — the difference is that the eye
 * always activates it (even if already active), while the body toggles it
 * off when it's already the active one. `selectOnly` carries that.
 */
const SegmentFilterChip = ({
  segment,
  isSelected,
  onClick
}: {
  segment: SegmentDescriptor;
  isSelected: boolean;
  onClick: (id: string, selectOnly: boolean) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { label } = segment;
  const showOnlyLabel = t('Show only items that reach {{ label }}', { label });
  return (
    <ActorFilterChipButton
      type="button"
      selected={isSelected}
      aria-pressed={isSelected}
      onClick={() => onClick(segment.id, false)}
    >
      <ActorChip color={segment.color} dimmed>
        <Tooltip title={isSelected ? t('Clear segment filter') : showOnlyLabel}>
          <span>{label}</span>
        </Tooltip>
        {isSelected && (
          <span id="af-icon">
            <Check size={16} />
          </span>
        )}
        <span id="af-eye">
          <Tooltip title={showOnlyLabel}>
            <span style={{ display: 'inline-flex' }}>
              <Eye
                size={16}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onClick(segment.id, true);
                }}
              />
            </span>
          </Tooltip>
        </span>
      </ActorChip>
    </ActorFilterChipButton>
  );
};

/**
 * "Filter by Saved Segment" row — sits directly under "Filter by Actor" and
 * mirrors its heading + wrapping pill row. Renders nothing at all (heading
 * included) when the template uses no saved segments, which also covers the
 * moment while the segment library is still loading.
 *
 * Selection is single-select: `onSelect(id)` to activate, `onSelect(null)`
 * when the active pill is clicked again. Mutual exclusivity with the actor
 * filter is the caller's job (it owns both pieces of state).
 */
export const SegmentFilterRow = React.memo(
  ({
    segments,
    selectedId,
    onSelect
  }: {
    segments: SegmentDescriptor[];
    selectedId: string | null;
    onSelect: (nextId: string | null) => void;
  }) => {
    const { t } = useUnsafeTranslation();
    const theme = useTheme();
    if (segments.length === 0) {
      return null;
    }
    const onChipClick = (id: string, selectOnly: boolean) => {
      if (selectOnly) {
        onSelect(id);
      } else {
        onSelect(id === selectedId ? null : id);
      }
    };
    return (
      <FlexContainer
        justifyContent="flex-start"
        alignItems="flex-start"
        direction="column"
        spacing={1}
      >
        <FlexContainer alignItems="center" spacing={0.5}>
          <Typography variant="h6" color="textSecondary">
            {t('Filter by Saved Segment')}
          </Typography>
          <Tooltip
            title={t('Save your segment to the library to enable filtering.')}
          >
            <HelpCircle size={16} color={theme.custom.colors.gray.w500} />
          </Tooltip>
        </FlexContainer>
        <FlexContainer wrap="wrap">
          {segments.map((s) => (
            <SegmentFilterChip
              key={s.id}
              segment={s}
              isSelected={s.id === selectedId}
              onClick={onChipClick}
            />
          ))}
        </FlexContainer>
      </FlexContainer>
    );
  }
);
